/**
 * Validation utilities for company information fields
 * Handles phone, email, URLs (social media), and CVR validation
 */

// ============================================
// PHONE VALIDATION
// ============================================

/**
 * Normalize a phone number by removing all non-digit characters except leading +
 */
export const normalizePhone = (phone: string): string => {
  if (!phone) return ''
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '')
  // Ensure + only appears at the start
  const plusIndex = cleaned.indexOf('+')
  if (plusIndex > 0) {
    return cleaned.replace(/\+/g, '')
  }
  return cleaned
}

/**
 * Format a phone number for display (Danish format)
 * Converts "12345678" to "12 34 56 78"
 */
export const formatPhoneDisplay = (phone: string): string => {
  const normalized = normalizePhone(phone)
  if (!normalized) return ''
  
  // Handle Danish numbers (8 digits, optionally with +45)
  if (/^(\+45)?[0-9]{8}$/.test(normalized)) {
    const digits = normalized.replace('+45', '')
    const formatted = digits.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')
    return normalized.startsWith('+45') ? `+45 ${formatted}` : formatted
  }
  
  // Return as-is for international numbers
  return normalized
}

/**
 * Validate a phone number
 * Accepts Danish 8-digit numbers with optional +45, or international numbers
 * Only allows: digits, spaces, dashes, dots, parentheses, and + at the start
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone || phone.trim() === '') return true // Empty is valid (optional field)
  
  // First check: Only allow valid phone characters (digits, spaces, dashes, dots, parentheses, +)
  // This catches letters like "51 41 46 46s"
  if (/[^0-9\s\-\.\(\)\+]/.test(phone)) {
    return false
  }
  
  const normalized = normalizePhone(phone)
  
  // Must have at least some digits
  if (!normalized || normalized.replace(/\+/g, '').length === 0) return false
  
  // Danish format: 8 digits, optionally with +45
  if (/^(\+45)?[0-9]{8}$/.test(normalized)) return true
  
  // International format: + followed by 10-15 digits
  if (/^\+[0-9]{10,15}$/.test(normalized)) return true
  
  // Allow just 8 digits without country code
  if (/^[0-9]{8}$/.test(normalized)) return true
  
  return false
}

export const getPhoneError = (phone: string): string | null => {
  if (!phone || phone.trim() === '') return null
  
  // Check for invalid characters first
  if (/[^0-9\s\-\.\(\)\+]/.test(phone)) {
    return 'Telefonnummer må kun indeholde tal, mellemrum og + tegn'
  }
  
  if (isValidPhone(phone)) return null
  return 'Ugyldigt telefonnummer. Brug 8 cifre (evt. med +45)'
}

// ============================================
// EMAIL VALIDATION
// ============================================

/**
 * Validate an email address
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || email.trim() === '') return true // Empty is valid (optional field)
  
  // Standard email regex - not too strict
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
  return emailRegex.test(email.trim().toLowerCase())
}

export const getEmailError = (email: string): string | null => {
  if (!email || email.trim() === '') return null
  if (isValidEmail(email)) return null
  return 'Ugyldig email-adresse'
}

// ============================================
// CVR VALIDATION
// ============================================

/**
 * Normalize CVR number (remove spaces)
 */
export const normalizeCVR = (cvr: string): string => {
  if (!cvr) return ''
  return cvr.replace(/\s/g, '')
}

/**
 * Validate Danish CVR number (exactly 8 digits)
 */
export const isValidCVR = (cvr: string): boolean => {
  if (!cvr || cvr.trim() === '') return true // Empty is valid (optional field)
  return /^[0-9]{8}$/.test(normalizeCVR(cvr))
}

export const getCVRError = (cvr: string): string | null => {
  if (!cvr || cvr.trim() === '') return null
  if (isValidCVR(cvr)) return null
  return 'CVR-nummer skal være 8 cifre'
}

// ============================================
// URL VALIDATION
// ============================================

/**
 * Basic URL validation
 */
export const isValidURL = (url: string): boolean => {
  if (!url || url.trim() === '') return true // Empty is valid
  
  try {
    const parsed = new URL(url.trim())
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    // Try adding https:// prefix
    try {
      const withProtocol = url.startsWith('www.') ? `https://${url}` : url
      new URL(withProtocol)
      return true
    } catch {
      return false
    }
  }
}

/**
 * Normalize URL by ensuring it has https:// prefix
 */
export const normalizeURL = (url: string): string => {
  if (!url || url.trim() === '') return ''
  
  const trimmed = url.trim()
  
  // Already has protocol
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed
  }
  
  // Add https://
  return `https://${trimmed}`
}

// ============================================
// SOCIAL MEDIA VALIDATION
// ============================================

/**
 * Validate Facebook URL
 */
export const isValidFacebookURL = (url: string): boolean => {
  if (!url || url.trim() === '') return true // Empty is valid
  
  const normalized = normalizeURL(url.trim().toLowerCase())
  // Allow facebook.com, fb.com, fb.me, m.facebook.com
  return /^https?:\/\/(www\.|m\.)?(facebook\.com|fb\.com|fb\.me)\/.+/i.test(normalized)
}

export const getFacebookError = (url: string): string | null => {
  if (!url || url.trim() === '') return null
  if (isValidFacebookURL(url)) return null
  return 'Ugyldig Facebook URL. Brug format: https://facebook.com/jeresfirma'
}

/**
 * Validate Instagram (URL or @username)
 */
export const isValidInstagram = (value: string): boolean => {
  if (!value || value.trim() === '') return true // Empty is valid
  
  const trimmed = value.trim()
  
  // Allow @username format (letters, numbers, underscores, periods)
  if (trimmed.startsWith('@')) {
    return /^@[\w.]{1,30}$/.test(trimmed)
  }
  
  // Allow just username without @
  if (/^[\w.]{1,30}$/.test(trimmed) && !trimmed.includes('.com')) {
    return true
  }
  
  // Allow full URL
  const normalized = normalizeURL(trimmed.toLowerCase())
  return /^https?:\/\/(www\.)?instagram\.com\/[\w.]+\/?/i.test(normalized)
}

/**
 * Normalize Instagram to @username format if possible
 */
export const normalizeInstagram = (value: string): string => {
  if (!value || value.trim() === '') return ''
  
  const trimmed = value.trim()
  
  // Already @username format
  if (trimmed.startsWith('@')) return trimmed
  
  // Extract username from URL
  const urlMatch = trimmed.match(/instagram\.com\/([\w.]+)/i)
  if (urlMatch) {
    return `@${urlMatch[1]}`
  }
  
  // Just a username, add @
  if (/^[\w.]{1,30}$/.test(trimmed)) {
    return `@${trimmed}`
  }
  
  return trimmed
}

export const getInstagramError = (value: string): string | null => {
  if (!value || value.trim() === '') return null
  if (isValidInstagram(value)) return null
  return 'Ugyldig Instagram. Brug @brugernavn eller https://instagram.com/brugernavn'
}

/**
 * Validate Google Business URL
 */
export const isValidGoogleBusinessURL = (url: string): boolean => {
  if (!url || url.trim() === '') return true // Empty is valid
  
  const normalized = normalizeURL(url.trim().toLowerCase())
  
  // Allow various Google URLs:
  // - google.com/maps/place/...
  // - maps.google.com/...
  // - goo.gl/maps/...
  // - g.page/...
  // - business.google.com/...
  // - search.google.com/local/...
  const patterns = [
    /^https?:\/\/(www\.)?google\.(com|dk|[a-z]{2})\/maps\/.+/i,
    /^https?:\/\/maps\.google\.(com|dk|[a-z]{2})\/.+/i,
    /^https?:\/\/goo\.gl\/maps\/.+/i,
    /^https?:\/\/g\.page\/.+/i,
    /^https?:\/\/(www\.)?business\.google\.com\/.+/i,
    /^https?:\/\/search\.google\.com\/local\/.+/i,
  ]
  
  return patterns.some(pattern => pattern.test(normalized))
}

export const getGoogleBusinessError = (url: string): string | null => {
  if (!url || url.trim() === '') return null
  if (isValidGoogleBusinessURL(url)) return null
  return 'Ugyldig Google Business URL. Brug linket fra jeres Google Maps profil'
}

/**
 * Validate Trustpilot URL
 */
export const isValidTrustpilotURL = (url: string): boolean => {
  if (!url || url.trim() === '') return true // Empty is valid
  
  const normalized = normalizeURL(url.trim().toLowerCase())
  
  // Allow trustpilot.com and trustpilot.dk with various language prefixes
  // - trustpilot.com/review/...
  // - dk.trustpilot.com/review/...
  // - www.trustpilot.com/review/...
  return /^https?:\/\/([a-z]{2}\.)?(www\.)?trustpilot\.(com|dk)\/(review\/)?/i.test(normalized)
}

export const getTrustpilotError = (url: string): string | null => {
  if (!url || url.trim() === '') return null
  if (isValidTrustpilotURL(url)) return null
  return 'Ugyldig Trustpilot URL. Brug format: https://dk.trustpilot.com/review/jeresfirma'
}

/**
 * Validate website URL
 */
export const isValidWebsiteURL = (url: string): boolean => {
  if (!url || url.trim() === '') return true // Empty is valid
  return isValidURL(url)
}

export const getWebsiteError = (url: string): string | null => {
  if (!url || url.trim() === '') return null
  if (isValidWebsiteURL(url)) return null
  return 'Ugyldig URL. Brug format: https://www.example.dk'
}

// ============================================
// COMBINED VALIDATION
// ============================================

export interface ValidationErrors {
  phone?: string | null
  email?: string | null
  contact_person_email?: string | null
  cvr_number?: string | null
  website?: string | null
  facebook_link?: string | null
  instagram?: string | null
  google_business_link?: string | null
  trustpilot?: string | null
  trustpilot_url?: string | null
}

/**
 * Validate all fields and return errors object
 */
export const validateCompanyFields = (data: {
  phone?: string
  email?: string
  contact_person_email?: string
  cvr_number?: string
  website?: string
  facebook_link?: string
  instagram?: string
  google_business_link?: string
  trustpilot?: string
  trustpilot_url?: string
}): ValidationErrors => {
  const errors: ValidationErrors = {}
  
  if (data.phone) errors.phone = getPhoneError(data.phone)
  if (data.email) errors.email = getEmailError(data.email)
  if (data.contact_person_email) errors.contact_person_email = getEmailError(data.contact_person_email)
  if (data.cvr_number) errors.cvr_number = getCVRError(data.cvr_number)
  if (data.website) errors.website = getWebsiteError(data.website)
  if (data.facebook_link) errors.facebook_link = getFacebookError(data.facebook_link)
  if (data.instagram) errors.instagram = getInstagramError(data.instagram)
  if (data.google_business_link) errors.google_business_link = getGoogleBusinessError(data.google_business_link)
  if (data.trustpilot) errors.trustpilot = getTrustpilotError(data.trustpilot)
  if (data.trustpilot_url) errors.trustpilot_url = getTrustpilotError(data.trustpilot_url)
  
  return errors
}

/**
 * Check if there are any validation errors
 */
export const hasValidationErrors = (errors: ValidationErrors): boolean => {
  return Object.values(errors).some(error => error !== null && error !== undefined)
}

/**
 * Normalize all fields before saving
 */
export const normalizeCompanyFields = (data: {
  phone?: string
  cvr_number?: string
  instagram?: string
  website?: string
  facebook_link?: string
  google_business_link?: string
  trustpilot?: string
  trustpilot_url?: string
}): typeof data => {
  const normalized = { ...data }
  
  if (data.phone) normalized.phone = normalizePhone(data.phone)
  if (data.cvr_number) normalized.cvr_number = normalizeCVR(data.cvr_number)
  // Don't normalize Instagram - keep as entered (URL or @username)
  // Don't auto-normalize URLs - user might have specific format
  
  return normalized
}

